/**
 * 配列取込 Worker（§0.1「Worker」・U152）。
 * FASTA / GenBank を読みながらチャンクを IndexedDB へ書き、進捗をメインスレッドへ返す。
 * 染色体級のファイルでも UI を止めない。
 */

import {
  DEFAULT_CHUNK_SIZE,
  iterateBlobBytes,
  parseFastaStream,
  parseGenBankStream,
} from '@kensuke/genome-core';
import {
  clearProject,
  importStagingProjectId,
  promoteImportStaging,
} from '../services/geneAnalysisChunkStore.service';
import { IndexedDbImportSink, type ImportedSequenceMeta } from '../services/geneAnalysisImportSink.service';

export type GeneAnalysisImportFormat = 'fasta' | 'genbank';

export interface GeneAnalysisImportRequest {
  type: 'IMPORT';
  projectId: string;
  file: File;
  format: GeneAnalysisImportFormat;
  chunkSize?: number;
  /** 同じプロジェクト ID の前回分を消してから取り込む */
  replace?: boolean;
}

export interface GeneAnalysisImportProgressMessage {
  type: 'PROGRESS';
  bytesRead: number;
  totalBytes: number;
  basesRead: number;
  sequenceName?: string;
}

export interface GeneAnalysisImportDoneMessage {
  type: 'DONE';
  sequences: ImportedSequenceMeta[];
  warnings: string[];
  elapsedMs: number;
}

export interface GeneAnalysisImportErrorMessage {
  type: 'ERROR';
  message: string;
}

export type GeneAnalysisImportResponse =
  | GeneAnalysisImportProgressMessage
  | GeneAnalysisImportDoneMessage
  | GeneAnalysisImportErrorMessage;

/** 拡張子から取込形式を決める（I20 / I21 の受付拡張子） */
export function detectImportFormat(fileName: string): GeneAnalysisImportFormat {
  const lower = fileName.toLowerCase();
  if (/\.(gb|gbk|genbank|gbff)$/.test(lower)) return 'genbank';
  return 'fasta';
}

export async function runGeneAnalysisImport(
  request: GeneAnalysisImportRequest,
  onProgress?: (progress: GeneAnalysisImportProgressMessage) => void
): Promise<GeneAnalysisImportDoneMessage> {
  const startedAt = Date.now();
  const replace = request.replace !== false;
  // 成功確定まで本体を消さない。失敗しても前回の配列が残る。
  const writeProjectId = replace ? importStagingProjectId(request.projectId) : request.projectId;
  await clearProject(writeProjectId);

  const chunkSize = request.chunkSize ?? DEFAULT_CHUNK_SIZE;
  const sink = new IndexedDbImportSink(writeProjectId, chunkSize);
  const bytes = iterateBlobBytes(request.file);
  const shared = {
    totalBytes: request.file.size,
    onProgress: (progress: { bytesRead: number; basesRead: number; sequenceName?: string }) => {
      onProgress?.({
        type: 'PROGRESS',
        bytesRead: progress.bytesRead,
        totalBytes: request.file.size,
        basesRead: progress.basesRead,
        sequenceName: progress.sequenceName,
      });
    },
  };

  try {
    const result =
      request.format === 'genbank'
        ? await parseGenBankStream(bytes, sink, shared)
        : await parseFastaStream(bytes, sink, shared);

    if (replace) {
      await promoteImportStaging(request.projectId, writeProjectId);
    }

    const sequences =
      replace
        ? sink.sequences.map((sequence) => ({ ...sequence, projectId: request.projectId }))
        : sink.sequences;

    return {
      type: 'DONE',
      sequences,
      warnings: result.warnings,
      elapsedMs: Date.now() - startedAt,
    };
  } catch (error) {
    // staging だけ捨てる。本体（request.projectId）は触らない。
    if (replace) {
      try {
        await clearProject(writeProjectId);
      } catch {
        // 掃除失敗は本エラーを優先する
      }
    }
    throw error;
  }
}

// Dedicated Worker として読み込まれたときだけ待ち受ける（テスト／メインからの import では掴まれない）
function isDedicatedWorkerScope(value: unknown): value is DedicatedWorkerGlobalScope {
  return typeof DedicatedWorkerGlobalScope !== 'undefined' && value instanceof DedicatedWorkerGlobalScope;
}

if (isDedicatedWorkerScope(self)) {
  self.onmessage = async (event: MessageEvent<GeneAnalysisImportRequest>) => {
    if (event.data?.type !== 'IMPORT') return;
    try {
      const done = await runGeneAnalysisImport(event.data, (progress) => self.postMessage(progress));
      self.postMessage(done);
    } catch (error) {
      const message: GeneAnalysisImportErrorMessage = {
        type: 'ERROR',
        message: error instanceof Error ? error.message : String(error),
      };
      self.postMessage(message);
    }
  };
}
